import { useEffect, useState } from 'react';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { Button } from '@components/button';
import { FormLabel } from '@components/form-label';
import { IconButton } from '@components/icon-button';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconTrash } from '@components/icons/trash';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { pxToRem } from '@utils/converters';

import { useNeedsTwoFactor } from 'features/authentication';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';

export const TwoFactor = observer(() => {
  const { userStore, t } = useStore();
  const { showModal } = useModal();
  const [state, setState] = useState({
    qrCodeUrl: '',
    code: null,
    codeError: '',
    phone: '',
    disable: false,
    authenticatorApp: false,
  });
  const needsTwoFactor = useNeedsTwoFactor();

  function enableAuthenticatorApp() {
    setState((old) => ({ ...old, authenticatorApp: true }));
  }

  function disableAuthenticatorApp() {
    setState((old) => ({ ...old, authenticatorApp: false }));
  }

  function enableSMS(phone) {
    setState((old) => ({ ...old, phone: phone }));
  }

  function disableSMS() {
    setState((old) => ({ ...old, phone: '' }));
  }

  function removeAuthenticatorModal() {
    showModal('REMOVE_AUTHENTICATOR', {
      disable: disableAuthenticatorApp,
      remove: userStore.removeAuthenticator,
    });
  }

  function addAuthenticatorModal() {
    showModal('ADD_AUTHENTICATOR', { qrCodeUrl: state.qrCodeUrl, enable: enableAuthenticatorApp });
  }

  function removeSMSModal() {
    showModal('REMOVE_AUTHENTICATOR', {
      disable: disableSMS,
      remove: userStore.removeSMSAuthenticator,
    });
  }

  function addSMSAuthenticatorModal() {
    showModal('ADD_SMS_AUTHENTICATOR', { enable: enableSMS });
  }

  useEffect(() => {
    if (needsTwoFactor) {
      showModal('NOTIFY_TWO_FACTOR');
    }
  }, [needsTwoFactor, showModal]);

  useEffect(() => {
    (async () => {
      const res = await userStore.getTwoFactorInfo();
      setState((old) => ({
        ...old,
        qrCodeUrl: res.data.qrCode || '',
        phone: res.data.phone,
        authenticatorApp: res.data.device,
      }));
    })();
  }, [userStore]);

  return (
    <Stack style={{ gap: pxToRem(16) }}>
      <Stack orientation="horizontal" justify="space-between">
        <div>
          <Text text={t('user.twofactor.heading')} font="bold16" />
          <Text text={t('user.twofactor.subheading')} font="regular14" color="textSecondary" />
        </div>
        <Stack justify="space-between" style={{ gap: pxToRem(16) }}>
          {!state.authenticatorApp && (
            <Button
              variant="secondary"
              onClick={addAuthenticatorModal}
              iconLeft={<IconAddPlus />}
              text={t('user.twofactor.button.addauthenticator')}
            />
          )}
          {!state.phone && (
            <Button
              variant="secondary"
              onClick={addSMSAuthenticatorModal}
              iconLeft={<IconAddPlus />}
              text={t('user.twofactor.button.addsms')}
            />
          )}
        </Stack>
      </Stack>
      <Stack style={{ gap: pxToRem(8) }}>
        {state.authenticatorApp && (
          <ConnectedContainer>
            <div>
              <FormLabel text={t('user.twofactor.label.authenticator')} />
              <Text
                color="textSecondary"
                font="regular14"
                text={t('user.twofactor.label.authenticator.connected')}
              />
            </div>
            <IconButton icon={<IconTrash />} onClick={removeAuthenticatorModal} />
          </ConnectedContainer>
        )}

        {state.phone && (
          <ConnectedContainer>
            <div>
              <FormLabel text={t('user.twofactor.label.phone')} />
              <Text color="textSecondary" font="regular14" text={state.phone} />
            </div>
            <IconButton icon={<IconTrash />} onClick={removeSMSModal} />
          </ConnectedContainer>
        )}
      </Stack>
    </Stack>
  );
});

const ConnectedContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
